import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import DarkModeToggle from './DarkModeToggle';
import { toast } from 'react-hot-toast';
import { stakeholderService } from '../services/stakeholder.service';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('[Layout] Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  const handleResetSession = async () => {
    if (window.confirm('Are you sure you want to reset your ENTIRE session? This will clear ALL conversations with ALL stakeholders, reset ALL progress, gaps, and points. This action cannot be undone.')) {
      const loadingToast = toast.loading('Resetting entire session...');

      try {
        await stakeholderService.resetAllSessions();
        toast.dismiss(loadingToast);
        toast.success('All sessions reset successfully!');
      } catch (error) {
        console.error('[Layout] Error resetting sessions:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to reset sessions on server, clearing local data...');
      }

      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-card border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold text-foreground">DigiStore Security Assessment</h1>
            </div>

            <nav className="flex items-center gap-3">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Home
              </Link>
              <Link to="/progress" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Progress
              </Link>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleResetSession}
              >
                Reset Session
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
              <DarkModeToggle />
            </nav>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
